<template>
  <v-container class="public-payment-auth fill-height" fluid>
    <v-row align="center" justify="center" style="min-width: 80vw;">
      <v-col cols="12" md="6">
        <v-card>

          <v-card-text>
            <!-- Loading state -->
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular
                indeterminate
                size="64"
                color="primary"
                class="mb-4"
              ></v-progress-circular>
              <div class="text-h6 mb-2">{{ $t('payment-method-ui.return-from-bank') }}</div>
              <div class="text-caption text--secondary">
                {{ $t('payment-method-ui.payment-processing') }}
              </div>
            </div>

            <!-- Success state -->
            <div v-else-if="success" class="text-center py-8">
              <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
              <div class="text-h6 mb-2">{{ $t('payment-method-ui.authentication-success') }}</div>
              <div class="text-body-2 text--secondary">
                {{ $t('payment-method-ui.you-can-close-window') }}
              </div>
            </div>

            <!-- Error state -->
            <div v-else-if="error" class="text-center py-8">
              <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
              <div class="text-h6 mb-2">{{ $t('payment-method-ui.authentication-failed') }}</div>
              <v-alert type="error" outlined class="mt-4">
                {{ errorMessage }}
              </v-alert>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeWindow">
              {{ $t('close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import StripeService from '@service/StripeService'

export default {
  name: 'PublicPaymentAuthentication',

  data() {
    return {
      loading: true,
      success: false,
      error: false,
      errorMessage: ''
    }
  },

  computed: {
    title() {
      if (this.loading) {
        return this.$t('payment-method-ui.payment-processing')
      }
      if (this.success) {
        return this.$t('payment-method-ui.authentication-success')
      }
      if (this.error) {
        return this.$t('payment-method-ui.authentication-failed')
      }
      return this.$t('payment-method-ui.payment')
    },

    statusIcon() {
      if (this.loading) {
        return { icon: 'mdi-loading mdi-spin', color: 'primary' }
      }
      if (this.success) {
        return { icon: 'mdi-check-circle', color: 'success' }
      }
      if (this.error) {
        return { icon: 'mdi-alert-circle', color: 'error' }
      }
      return { icon: 'mdi-shield-lock', color: 'primary' }
    },

    paymentIntentClientSecret() {
      // Stripe passes client_secret in URL as payment_intent_client_secret
      // Also accept client_secret as a direct parameter
      return this.$route.query.payment_intent_client_secret || this.$route.query.client_secret
    }
  },

  mounted() {
    this.handleReturn()
  },

  methods: {
    async handleReturn() {
      this.loading = true

      try {
        const clientSecret = this.paymentIntentClientSecret
        if (!clientSecret) {
          throw new Error('No client secret found in URL')
        }

        // First, retrieve the payment intent to check its status
        let result = await StripeService.retrievePaymentIntent(clientSecret)

        if (result.error) {
          this.error = true
          this.errorMessage = result.error.message || this.$t('error')
          return
        }

        // If still requires action, trigger the authentication
        if (result.paymentIntent && result.paymentIntent.status === 'requires_action') {
          result = await StripeService.handleNextAction(clientSecret)
        }

        if (result.error) {
          this.error = true
          this.errorMessage = result.error.message || this.$t('error')
        } else if (result.paymentIntent) {
          await this.checkPaymentIntentStatus(result.paymentIntent)
        }
      } catch (err) {
        this.error = true
        this.errorMessage = err.message || this.$t('error')
      } finally {
        this.loading = false
      }
    },

    async checkPaymentIntentStatus(paymentIntent) {
      switch (paymentIntent.status) {
        case 'succeeded':
        case 'processing':
          // Success
          this.success = true
          break

        case 'requires_capture':
          // Authentication succeeded, now capture the payment
          await this.capturePayment(paymentIntent.id)
          break

        case 'requires_payment_method':
          this.error = true
          this.errorMessage = this.$t('payment-method-ui.status.requires_payment_method')
          break

        case 'canceled':
          this.error = true
          this.errorMessage = this.$t('payment-method-ui.authentication-canceled')
          break

        case 'failed':
          this.error = true
          this.errorMessage = this.$t('payment-method-ui.status.failed')
          break

        case 'requires_action':
          // Still requires action (shouldn't happen after redirect)
          this.error = true
          this.errorMessage = this.$t('payment-method-ui.status.requires_action')
          break

        default:
          this.error = true
          this.errorMessage = `Unexpected status: ${paymentIntent.status}`
      }
    },

    async capturePayment(paymentIntentId) {
      try {
        this.loading = true
        this.loadingMessage = this.$t('payment-method-ui.capturing-payment')

        // Import dynamically to avoid issues if not authenticated
        const PaymentMethodRepository = (await import('@repository/PaymentMethodRepository')).default

        // Capture the full amount
        const result = await PaymentMethodRepository.capturePayment(paymentIntentId, {
          final_capture: true
        })

        // Check the new status
        if (result.status === 'succeeded') {
          this.success = true
        } else {
          this.error = true
          this.errorMessage = `Payment captured but status is: ${result.status}`
        }
      } catch (err) {
        this.error = true
        this.errorMessage = err?.response?.data?.message || err.message || this.$t('payment-method-ui.capture-failed')
      } finally {
        this.loading = false
      }
    },

    closeWindow() {
      // Try to close window/tab
      window.close()

      // If window.close() doesn't work (might not work if not opened by script),
      // just show a message
      setTimeout(() => {
        if (!window.closed) {
          alert(this.$t('payment-method-ui.you-can-close-window'))
        }
      }, 100)
    }
  }
}
</script>

<style lang="scss">
.public-payment-auth {

}

.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
